import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';
import { bootGrScrollEngine, GrScrollEngine } from '@theme/gr-scroll-engine';

/**
 * Landing / preview story panel.
 *
 * Visibility is intentionally dumb:
 * - show() always runs the mask appear (no hero-busy / covering gates)
 * - hide() only when the section has actually left the viewport
 * - intro-complete / enter / enterBack always call show()
 */
class GrLandingStory extends Component {
  #revealTrigger = null;
  #blurTrigger = null;
  #snapBackTrigger = null;
  #snapForwardTrigger = null;
  #blurInner = null;
  #blurChevron = null;
  #snapBackAnimating = false;
  #snapForwardAnimating = false;
  #snapForwardDone = false;
  #textRevealed = false;
  #cueJumpGraceUntil = 0;
  #followsCarousel = false;
  #showToken = 0;

  #onBlurResize = () => {
    this.#fitCopy();
  };

  connectedCallback() {
    super.connectedCallback();

    if (this.#shouldContentReveal()) {
      this.setAttribute('data-scroll-reveal', '');
    } else {
      this.classList.add('is-revealed');
    }

    window.addEventListener('gr-scroll:ready', this.#onScrollReady);
    window.addEventListener('gr-hero:intro-complete', this.#onHeroIntroComplete);
    window.addEventListener('gr-hero:reinit', this.#onHeroReinit);
    window.addEventListener('gr-scroll:carousel-cover', this.#onCarouselCover);
    window.addEventListener('gr-scroll:cue-jump', this.#onCueJump);
    bootGrScrollEngine().then((engine) => {
      if (engine) this.#onScrollReady();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('gr-scroll:ready', this.#onScrollReady);
    window.removeEventListener('gr-hero:intro-complete', this.#onHeroIntroComplete);
    window.removeEventListener('gr-hero:reinit', this.#onHeroReinit);
    window.removeEventListener('gr-scroll:carousel-cover', this.#onCarouselCover);
    window.removeEventListener('gr-scroll:cue-jump', this.#onCueJump);
    this.#killBlurTweens();
    this.#blurTrigger?.kill();
    this.#snapBackTrigger?.kill();
    this.#snapForwardTrigger?.kill();
    this.#revealTrigger?.kill();
    this.#blurTrigger = null;
    this.#snapBackTrigger = null;
    this.#snapForwardTrigger = null;
    this.#revealTrigger = null;
    this.#clearBlurStyles();
  }

  #onScrollReady = () => {
    if (this.#initBlurTextTransition()) return;
    this.#initContentReveal();
  };

  #onCueJump = () => {
    this.#snapForwardDone = true;
    this.#snapForwardAnimating = false;
    this.#cueJumpGraceUntil = performance.now() + 1400;
    this.#show();
  };

  #onCarouselCover = () => {
    if (!this.#followsCarousel) return;
    if (!this.hasAttribute('data-gr-blur-text')) return;
    if (this.classList.contains('is-blur-active')) return;
    this.#show();
  };

  #onHeroIntroComplete = () => {
    if (!this.hasAttribute('data-gr-blur-text')) return;
    this.#show();
  };

  #onHeroReinit = () => {
    this.#snapForwardDone = false;
    this.#hide(true);
  };

  #shouldContentReveal() {
    return (
      document.documentElement.hasAttribute('data-gr-scroll') &&
      this.hasAttribute('data-enable-reveal') &&
      !prefersReducedMotion()
    );
  }

  #canBlurTransition() {
    return (
      !this.hasAttribute('data-no-blur-text') &&
      document.documentElement.hasAttribute('data-gr-scroll') &&
      this.hasAttribute('data-enable-reveal') &&
      !prefersReducedMotion()
    );
  }

  #killBlurTweens() {
    const gsap = GrScrollEngine.get()?.gsap;
    if (!gsap) return;
    if (this.#blurInner) gsap.killTweensOf(this.#blurInner);
    if (this.#blurChevron) gsap.killTweensOf(this.#blurChevron);
  }

  #clearBlurStyles() {
    window.removeEventListener('resize', this.#onBlurResize);
    const inner = this.querySelector('.gr-landing-story__inner');
    const chevron = this.querySelector('.gr-landing-story__chevron');
    if (inner instanceof HTMLElement) {
      inner.style.removeProperty('--gr-mask-size');
      inner.style.removeProperty('--gr-blur-fit');
      inner.style.removeProperty('opacity');
    }
    if (chevron instanceof HTMLElement) {
      chevron.style.removeProperty('opacity');
    }
    this.classList.remove('is-blur-active');
    this.removeAttribute('data-gr-blur-text');
    document.querySelectorAll('[data-gr-after-blur-text]').forEach((el) => {
      el.removeAttribute('data-gr-after-blur-text');
    });
    this.#blurInner = null;
    this.#blurChevron = null;
    this.#textRevealed = false;
  }

  /**
   * Scale copy to fit under the logo / above the chevron.
   * Only on init + resize — never on show() (avoids 2nd-visit jumps).
   */
  #fitCopy = () => {
    const inner = this.#blurInner;
    if (!(inner instanceof HTMLElement) || !this.hasAttribute('data-gr-blur-text')) return;

    inner.style.setProperty('--gr-blur-fit', '1');
    void inner.offsetHeight;

    const styles = getComputedStyle(this);
    const topSafe = Number.parseFloat(styles.getPropertyValue('--gr-blur-top')) || 132;
    const bottomSafe = Number.parseFloat(styles.getPropertyValue('--gr-blur-bottom')) || 44;
    const available = Math.max(180, window.innerHeight - topSafe - bottomSafe);
    const content = inner.scrollHeight;
    if (content <= 0) return;

    const scale =
      content > available ? Math.max(0.55, Math.min(1, available / content)) : 1;
    inner.style.setProperty('--gr-blur-fit', String(Number(scale.toFixed(4))));
  };

  #initContentReveal = () => {
    if (this.hasAttribute('data-gr-blur-text')) return;
    if (!this.#shouldContentReveal() || this.#revealTrigger) return;
    if (this.classList.contains('is-revealed')) return;

    const engine = GrScrollEngine.get();
    const ScrollTrigger = engine?.ScrollTrigger;
    if (!ScrollTrigger) {
      this.#initRevealObserver();
      return;
    }

    this.setAttribute('data-scroll-reveal', '');

    const reveal = () => {
      if (this.classList.contains('is-revealed')) return;
      this.classList.add('is-revealed');
    };

    this.#revealTrigger = ScrollTrigger.create({
      trigger: this,
      start: 'top 85%',
      end: 'bottom top',
      invalidateOnRefresh: true,
      onEnter: reveal,
      onEnterBack: reveal,
      // Keep revealed — toggling off at the threshold snapped content 24px on mobile.
    });

    requestAnimationFrame(() => {
      engine?.refresh?.();
      if (this.#revealTrigger && this.#revealTrigger.progress > 0) {
        reveal();
      }
    });
  };

  #initRevealObserver = () => {
    if (this.#revealTrigger || this.classList.contains('is-revealed')) return;
    this.setAttribute('data-scroll-reveal', '');

    if (typeof IntersectionObserver !== 'function') {
      this.classList.add('is-revealed');
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        this.classList.add('is-revealed');
        io.disconnect();
      },
      { threshold: 0.2, rootMargin: '0px 0px -10% 0px' }
    );
    io.observe(this);
    this.#revealTrigger = /** @type {any} */ ({ kill: () => io.disconnect() });
  };

  #isProgrammaticScroll = () =>
    Boolean(window.__grProgrammaticScroll || window.__grHeroAnimating) ||
    (this.#cueJumpGraceUntil > 0 && performance.now() < this.#cueJumpGraceUntil);

  /** Section still intersects the viewport enough to keep copy. */
  #sectionVisible = () => {
    const rect = this.getBoundingClientRect();
    return rect.bottom > 80 && rect.top < window.innerHeight - 80;
  };

  /**
   * Mask appear. Never run mid hero-intro — that interrupted the white fade
   * cover and made the panel snap to solid white halfway.
   */
  #show = () => {
    if (this.#snapBackAnimating) return;
    if (document.documentElement.classList.contains('gr-hero-intro-active')) return;

    const gsap = GrScrollEngine.get()?.gsap;
    const inner = this.#blurInner;
    if (!gsap || !inner) return;

    const token = ++this.#showToken;
    this.classList.add('is-blur-active');
    this.#textRevealed = true;

    gsap.killTweensOf(inner);
    if (this.#blurChevron) gsap.killTweensOf(this.#blurChevron);

    gsap.fromTo(
      inner,
      { '--gr-mask-size': 0, opacity: 0 },
      {
        '--gr-mask-size': 300,
        opacity: 1,
        duration: 2.5,
        ease: 'power2.out',
        overwrite: true,
        onComplete: () => {
          if (token !== this.#showToken) return;
          // Ensure final state sticks even if a kill raced mid-tween.
          gsap.set(inner, { opacity: 1, '--gr-mask-size': 300 });
        },
      }
    );

    if (this.#blurChevron) {
      gsap.fromTo(
        this.#blurChevron,
        { opacity: 0 },
        { opacity: 1, duration: 0.6, ease: 'power2.out', overwrite: true }
      );
    }
  };

  /** @param {boolean} [instant] */
  #hide = (instant = false) => {
    this.#showToken += 1;
    this.#textRevealed = false;
    this.classList.remove('is-blur-active');

    const gsap = GrScrollEngine.get()?.gsap;
    const inner = this.#blurInner;

    if (gsap && inner) {
      gsap.killTweensOf(inner);
      if (this.#blurChevron) gsap.killTweensOf(this.#blurChevron);

      if (instant) {
        gsap.set(inner, { opacity: 0, '--gr-mask-size': 0 });
        if (this.#blurChevron) gsap.set(this.#blurChevron, { opacity: 0 });
        return;
      }

      gsap.to(inner, {
        '--gr-mask-size': 0,
        opacity: 0,
        duration: 0.7,
        ease: 'power2.out',
        overwrite: true,
      });
      if (this.#blurChevron) gsap.set(this.#blurChevron, { opacity: 0 });
      return;
    }

    if (inner) {
      inner.style.opacity = '0';
      inner.style.setProperty('--gr-mask-size', '0');
    }
    if (this.#blurChevron) this.#blurChevron.style.opacity = '0';
  };

  #hideIfLeft = () => {
    if (this.#isProgrammaticScroll()) return;
    if (this.#sectionVisible()) return;
    this.#hide(true);
  };

  #snapStoryIntoView = () => {
    if (this.#snapForwardAnimating || this.#snapForwardDone) return;
    if (window.__grProgrammaticScroll) return;
    if (window.__grHeroAnimating) return;
    if (document.documentElement.classList.contains('gr-hero-intro-active')) return;

    const engine = GrScrollEngine.get();
    if (!engine) {
      this.#show();
      return;
    }

    const section = this.closest('.shopify-section') || this;
    const currentY =
      typeof engine.lenis?.scroll === 'number' ? engine.lenis.scroll : window.scrollY || 0;
    const top = Math.round(currentY + section.getBoundingClientRect().top);

    // On touch, skip forced snap — it fights the finger and feels like a jump.
    const coarse =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(hover: none), (pointer: coarse)').matches;
    if (coarse || Math.abs(currentY - top) < 80) {
      this.#snapForwardDone = true;
      this.#show();
      return;
    }

    this.#snapForwardAnimating = true;
    window.__grHeroAnimating = true;

    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      this.#snapForwardAnimating = false;
      this.#snapForwardDone = true;
      window.__grHeroAnimating = false;
      this.#show();
    };

    const safety = window.setTimeout(finish, 1200);

    engine.scrollTo(top, {
      force: true,
      duration: 0.7,
      onComplete: () => {
        window.clearTimeout(safety);
        finish();
      },
    });
  };

  #initBlurTextTransition = () => {
    if (this.#blurTrigger || !this.#canBlurTransition()) return false;

    const engine = GrScrollEngine.get();
    const gsap = engine?.gsap;
    const ScrollTrigger = engine?.ScrollTrigger;
    if (!gsap || !ScrollTrigger) return false;

    const storySection = this.closest('.shopify-section');
    const nextSection = storySection?.nextElementSibling;
    if (!(nextSection instanceof HTMLElement)) return false;

    const inner = this.querySelector('.gr-landing-story__inner');
    const chevron = this.querySelector('.gr-landing-story__chevron');
    if (!(inner instanceof HTMLElement)) return false;

    this.removeAttribute('data-scroll-reveal');
    this.classList.remove('is-revealed');

    this.setAttribute('data-gr-blur-text', '');
    nextSection.setAttribute('data-gr-after-blur-text', '');
    nextSection.querySelector('gr-selection-module')?.setAttribute('data-gr-after-blur-text', '');

    this.#blurInner = inner;
    this.#blurChevron = chevron instanceof HTMLElement ? chevron : null;
    this.#textRevealed = false;
    this.#snapForwardDone = false;

    gsap.set(inner, { '--gr-mask-size': 0, opacity: 0 });
    if (this.#blurChevron) gsap.set(this.#blurChevron, { opacity: 0 });

    this.#fitCopy();
    window.addEventListener('resize', this.#onBlurResize, { passive: true });

    const hasHero = Boolean(document.querySelector('gr-hero'));
    const followsCarousel = Boolean(
      storySection?.previousElementSibling?.querySelector('[data-gr-scroll-carousel]')
    );
    this.#followsCarousel = followsCarousel;

    // After intro: show when panel covers (~top 8%, same as branding-preview).
    // During intro, only gr-hero:intro-complete may call #show().
    this.#blurTrigger = ScrollTrigger.create({
      trigger: this,
      start: 'top 8%',
      end: 'bottom top',
      invalidateOnRefresh: true,
      onEnter: () => {
        // Carousel handoff in flight: it fires gr-scroll:carousel-cover on arrival.
        if (this.#followsCarousel && window.__grHeroAnimating) return;
        this.#show();
      },
      onEnterBack: () => {
        if (this.#snapBackAnimating) return;
        if (this.#isProgrammaticScroll()) return;
        this.#show();
      },
      onLeave: () => this.#hideIfLeft(),
      onLeaveBack: () => {
        if (!window.__grProgrammaticScroll) {
          this.#snapForwardDone = false;
        }
        // Always hard-hide on the way back to hero — sticky pin would otherwise
        // keep copy painted over the video (hideIfLeft is too soft here).
        this.#hide(true);
      },
    });

    if (!hasHero && !followsCarousel) {
      this.#snapForwardTrigger = ScrollTrigger.create({
        trigger: this,
        start: 'top 95%',
        end: 'bottom top',
        invalidateOnRefresh: true,
        onEnter: () => this.#snapStoryIntoView(),
        onLeaveBack: () => {
          if (window.__grProgrammaticScroll) return;
          this.#snapForwardDone = false;
        },
      });
    }

    if (document.body.classList.contains('template-the-abyss') && hasHero) {
      this.#snapBackTrigger = ScrollTrigger.create({
        trigger: this,
        start: 'top top',
        end: 'bottom top',
        onLeaveBack: (self) => {
          if (window.__grHeroAnimating) return;
          if (document.documentElement.classList.contains('gr-hero-intro-active')) return;
          if (this.#snapBackAnimating) return;
          if (self.direction !== -1) return;
          if (window.scrollY <= 0) return;

          this.#hide(true);
          this.#snapBackAnimating = true;
          window.__grHeroAnimating = true;

          engine.scrollTo(0, {
            force: true,
            duration: 1.2,
            onComplete: () => {
              this.#hide(true);
              this.#snapBackAnimating = false;
              window.__grHeroAnimating = false;
              window.dispatchEvent(new CustomEvent('gr-hero:reinit'));
            },
          });
        },
      });
    }

    requestAnimationFrame(() => {
      engine.refresh?.();
      if (this.#blurTrigger?.isActive) this.#show();
    });

    return true;
  };
}

if (!customElements.get('gr-landing-story')) {
  customElements.define('gr-landing-story', GrLandingStory);
}
